/**
 * Mobilni meni (< 1024px) — brez jQuery/Bootstrap odvisnosti.
 *
 * - burger [data-main-nav-toggle] odpre/zapre panel [data-main-nav-panel]
 * - zaklep scrolla strani (html.main-nav-open → body position: fixed) + vrnitev na
 *   prejšnji scroll položaj ob zaprtju (brez "smooth" animacije)
 * - vsebina strani izven headerja dobi `inert` (ni dosegljiva s tabom/klikom)
 * - accordion podmeniji: za vsak .menu-item-has-children doda gumb s puščico,
 *   trenutna sekcija (current-menu-*) je privzeto razprta
 * - Esc zapre, fokus se vrne na burger; preklop na desktop širino zapre meni;
 *   bfcache (nazaj) ne pusti odprtega menija
 *
 * Stili: src/styles/_main-nav.scss
 */

const DESKTOP_MQ = '(min-width: 1024px)'
const SUB_ANIM_MS = 280 // mora ustrezati $nav-anim v _main-nav.scss

const CHEVRON_SVG =
	'<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">' +
	'<path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
	'</svg>'

function onMediaChange(mq, fn) {
	if (mq.addEventListener) {
		mq.addEventListener('change', fn)
	} else if (mq.addListener) {
		mq.addListener(fn) // Safari < 14
	}
}

function isSamePageAnchor(link) {
	return (
		link.hash !== '' &&
		link.origin === window.location.origin &&
		link.pathname === window.location.pathname &&
		link.search === window.location.search
	)
}

export default {
	init() {
		const burger = document.querySelector('[data-main-nav-toggle]')
		const panel = document.querySelector('[data-main-nav-panel]')
		if (!burger || !panel) return

		const html = document.documentElement
		const header = document.getElementById('stickyHeader')
		const desktopMq = window.matchMedia(DESKTOP_MQ)
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

		const labels = {
			open: burger.dataset.labelOpen || 'Odpri meni',
			close: burger.dataset.labelClose || 'Zapri meni',
			subOpen: panel.dataset.labelSubOpen || 'Prikaži podmeni',
			subClose: panel.dataset.labelSubClose || 'Skrij podmeni',
		}

		let isOpen = false
		let savedScrollY = 0
		let inertEls = []

		/* ---------------- zaklep strani ---------------- */

		const lockPage = () => {
			savedScrollY = window.scrollY || window.pageYOffset || 0

			// širina scrollbara (na touch napravah 0) → kompenzacija v CSS
			const scrollbarWidth = window.innerWidth - html.clientWidth
			html.style.setProperty('--main-nav-sbw', (scrollbarWidth > 0 ? scrollbarWidth : 0) + 'px')
			html.classList.add('main-nav-open')

			// vse razen headerja naj bo nedosegljivo (tab/klik/AT)
			inertEls = Array.from(document.body.children).filter((el) => {
				if (el === header || (header && el.contains(header))) return false
				if (el.hasAttribute('inert')) return false
				return !/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(el.tagName)
			})
			inertEls.forEach((el) => { el.inert = true })
		}

		const unlockPage = () => {
			inertEls.forEach((el) => { el.inert = false })
			inertEls = []

			html.classList.remove('main-nav-open')
			html.style.removeProperty('--main-nav-sbw')

			// html ima scroll-behavior: smooth → začasno izklopi, da ni vidnega drsenja
			const prevBehavior = html.style.scrollBehavior
			html.style.scrollBehavior = 'auto'
			window.scrollTo(0, savedScrollY)
			html.style.scrollBehavior = prevBehavior
		}

		/* ---------------- odpri / zapri ---------------- */

		const onKeydown = (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault()
				close()
			}
		}

		const open = () => {
			if (isOpen || desktopMq.matches) return
			isOpen = true

			lockPage()
			panel.classList.add('is-open')
			burger.setAttribute('aria-expanded', 'true')
			burger.setAttribute('aria-label', labels.close)
			document.addEventListener('keydown', onKeydown)

			panel.focus({ preventScroll: true })
		}

		const close = ({ restoreFocus = true } = {}) => {
			if (!isOpen) return
			isOpen = false

			panel.classList.remove('is-open')
			burger.setAttribute('aria-expanded', 'false')
			burger.setAttribute('aria-label', labels.open)
			document.removeEventListener('keydown', onKeydown)
			unlockPage()

			if (restoreFocus) burger.focus({ preventScroll: true })
		}

		burger.addEventListener('click', () => (isOpen ? close() : open()))

		// klik na sidro znotraj iste strani → zapri (navadne povezave naložijo novo stran)
		panel.addEventListener('click', (e) => {
			const link = e.target.closest('a[href]')
			if (!link || !isOpen) return
			if (isSamePageAnchor(link)) close({ restoreFocus: false })
		})

		// preklop na desktop širino → zapri in počisti inline višine podmenijev
		onMediaChange(desktopMq, (e) => {
			if (!e.matches) return
			close({ restoreFocus: false })
			subItems.forEach((item) => { item.sub.style.height = '' })
		})

		// bfcache (vrnitev z "nazaj") ne sme pustiti odprtega menija
		window.addEventListener('pageshow', (e) => {
			if (e.persisted) close({ restoreFocus: false })
		})

		/* ---------------- accordion podmeniji ---------------- */

		const subItems = []

		const setSub = (item, expanded, animate) => {
			const { li, btn, sub } = item

			btn.setAttribute('aria-expanded', expanded ? 'true' : 'false')
			btn.setAttribute('aria-label', (expanded ? labels.subClose : labels.subOpen) + ': ' + item.title)

			const doAnimate = animate && !reduceMotion.matches && !desktopMq.matches
			clearTimeout(item.timer)

			if (!doAnimate) {
				li.classList.toggle('is-open', expanded)
				sub.style.height = ''
				return
			}

			// CSS ne zna animirati height: 0 ↔ auto, zato prehod prek izmerjene višine
			if (expanded) {
				sub.style.height = '0px'
				li.classList.add('is-open')
				void sub.offsetHeight // reflow
				sub.style.height = sub.scrollHeight + 'px'
			} else {
				sub.style.height = sub.scrollHeight + 'px'
				void sub.offsetHeight
				li.classList.remove('is-open')
				sub.style.height = '0px'
			}

			item.timer = setTimeout(() => { sub.style.height = '' }, SUB_ANIM_MS + 50)
		}

		panel.querySelectorAll('.main-nav__list > li.menu-item-has-children').forEach((li, i) => {
			const link = li.querySelector(':scope > a')
			const sub = li.querySelector(':scope > ul')
			if (!link || !sub) return

			if (!sub.id) sub.id = (li.id || 'main-nav-item-' + i) + '-sub'

			const btn = document.createElement('button')
			btn.type = 'button'
			btn.className = 'main-nav__sub-toggle'
			btn.setAttribute('aria-controls', sub.id)
			btn.innerHTML = CHEVRON_SVG

			const item = { li, btn, sub, title: link.textContent.trim(), timer: null }
			subItems.push(item)

			// trenutna sekcija je privzeto razprta
			const isCurrent =
				li.classList.contains('current-menu-item') ||
				li.classList.contains('current-menu-parent') ||
				li.classList.contains('current-menu-ancestor')
			setSub(item, isCurrent, false)

			link.insertAdjacentElement('afterend', btn)
			btn.addEventListener('click', () => setSub(item, !li.classList.contains('is-open'), true))

			// starševska povezava brez cilja (# ali prazno) na mobilu samo razpre podmeni
			const href = (link.getAttribute('href') || '').trim()
			if (href === '' || href === '#') {
				link.addEventListener('click', (e) => {
					if (desktopMq.matches) return
					e.preventDefault()
					setSub(item, !li.classList.contains('is-open'), true)
				})
			}
		})
	},
}
